import { ApiError } from '../app.models';

/**
 * Base application error class
 * All custom application errors should extend this class
 */
export abstract class AppError extends Error {
  public readonly statusCode: number;
  public readonly code: string;
  public readonly userMessage: string;
  public readonly originalError?: Error | null;
  public readonly timestamp: Date;
  public readonly traceId?: string;

  constructor(
    message: string,
    statusCode: number,
    code: string,
    userMessage: string,
    originalError?: Error | null,
    traceId?: string,
  ) {
    super(message);
    this.name = this.constructor.name;
    this.statusCode = statusCode;
    this.code = code;
    this.userMessage = userMessage;
    this.originalError = originalError;
    this.timestamp = new Date();
    this.traceId = traceId;

    // Maintains proper stack trace in V8 environments (Node.js/Chrome)
    const captureStackTrace = (
      Error as {
        captureStackTrace?: (
          target: object,
          constructor: new (...args: unknown[]) => unknown,
        ) => void;
      }
    ).captureStackTrace;
    if (typeof captureStackTrace === 'function') {
      captureStackTrace(this, this.constructor as new (...args: unknown[]) => unknown);
    }
  }

  /**
   * Convert error to API error format for logging/reporting
   */
  toApiError(): ApiError {
    return {
      Code: this.code,
      Message: this.message,
      Target: this.traceId,
    };
  }

  /**
   * Create a user-friendly error info object for error pages
   */
  toErrorInfo(): ErrorInfo {
    return {
      statusCode: this.statusCode,
      code: this.code,
      title: this.getTitle(),
      message: this.userMessage,
      timestamp: this.timestamp,
      traceId: this.traceId,
    };
  }

  /**
   * Get the display title for the error
   */
  abstract getTitle(): string;

  /**
   * Check if this error should be reported to error tracking service
   */
  shouldReport(): boolean {
    return this.statusCode >= 500;
  }
}

/**
 * User-friendly error info for display on error pages
 */
export interface ErrorInfo {
  statusCode: number;
  code: string;
  title: string;
  message: string;
  timestamp: Date;
  traceId?: string;
}

/**
 * 400 Bad Request Error
 */
export class BadRequestError extends AppError {
  constructor(message: string, originalError?: Error, traceId?: string) {
    super(
      message,
      400,
      'BAD_REQUEST',
      'The request was invalid. Please check your input and try again.',
      originalError,
      traceId,
    );
  }

  getTitle(): string {
    return 'Bad Request';
  }
}

/**
 * 401 Unauthorized Error
 */
export class UnauthorizedError extends AppError {
  constructor(message = 'Authentication required', originalError?: Error, traceId?: string) {
    super(
      message,
      401,
      'UNAUTHORIZED',
      'You need to be logged in to access this resource. Please log in and try again.',
      originalError,
      traceId,
    );
  }

  getTitle(): string {
    return 'Unauthorized';
  }
}

/**
 * 403 Forbidden Error
 */
export class ForbiddenError extends AppError {
  constructor(message = 'Access denied', originalError?: Error, traceId?: string) {
    super(
      message,
      403,
      'FORBIDDEN',
      'You do not have permission to access this resource.',
      originalError,
      traceId,
    );
  }

  getTitle(): string {
    return 'Access Denied';
  }
}

/**
 * 404 Not Found Error
 */
export class NotFoundError extends AppError {
  constructor(message = 'Resource not found', originalError?: Error, traceId?: string) {
    super(
      message,
      404,
      'NOT_FOUND',
      'The page or resource you are looking for could not be found.',
      originalError,
      traceId,
    );
  }

  getTitle(): string {
    return 'Page Not Found';
  }
}

/**
 * 409 Conflict Error
 */
export class ConflictError extends AppError {
  constructor(message: string, originalError?: Error, traceId?: string) {
    super(
      message,
      409,
      'CONFLICT',
      'A conflict occurred. The resource may have been modified by another user.',
      originalError,
      traceId,
    );
  }

  getTitle(): string {
    return 'Conflict';
  }
}

/**
 * 422 Unprocessable Entity Error (Validation Error)
 */
export class ValidationError extends AppError {
  public readonly validationErrors: Record<string, string[]>;

  constructor(
    message: string,
    validationErrors: Record<string, string[]> = {},
    originalError?: Error,
    traceId?: string,
  ) {
    super(
      message,
      422,
      'VALIDATION_ERROR',
      'Please check the form for errors and try again.',
      originalError,
      traceId,
    );
    this.validationErrors = validationErrors;
  }

  getTitle(): string {
    return 'Validation Error';
  }

  override toErrorInfo(): ErrorInfo & { validationErrors: Record<string, string[]> } {
    return {
      ...super.toErrorInfo(),
      validationErrors: this.validationErrors,
    };
  }
}

/**
 * 429 Too Many Requests Error
 */
export class TooManyRequestsError extends AppError {
  public readonly retryAfter?: number;

  constructor(
    message = 'Too many requests',
    retryAfter?: number,
    originalError?: Error,
    traceId?: string,
  ) {
    super(
      message,
      429,
      'TOO_MANY_REQUESTS',
      'Too many requests. Please wait a moment and try again.',
      originalError,
      traceId,
    );
    this.retryAfter = retryAfter;
  }

  getTitle(): string {
    return 'Too Many Requests';
  }
}

/**
 * 500 Internal Server Error
 */
export class ServerError extends AppError {
  constructor(message = 'Internal server error', originalError?: Error, traceId?: string) {
    super(
      message,
      500,
      'INTERNAL_SERVER_ERROR',
      'Something went wrong on our end. Our team has been notified. Please try again later.',
      originalError,
      traceId,
    );
  }

  getTitle(): string {
    return 'Server Error';
  }
}

/**
 * 502 Bad Gateway Error
 */
export class BadGatewayError extends AppError {
  constructor(message = 'Bad gateway', originalError?: Error, traceId?: string) {
    super(
      message,
      502,
      'BAD_GATEWAY',
      'The server received an invalid response from an upstream server. Please try again later.',
      originalError,
      traceId,
    );
  }

  getTitle(): string {
    return 'Bad Gateway';
  }
}

/**
 * 503 Service Unavailable Error
 */
export class ServiceUnavailableError extends AppError {
  public readonly retryAfter?: number;

  constructor(
    message = 'Service unavailable',
    retryAfter?: number,
    originalError?: Error,
    traceId?: string,
  ) {
    super(
      message,
      503,
      'SERVICE_UNAVAILABLE',
      'The service is temporarily unavailable. Please try again later.',
      originalError,
      traceId,
    );
    this.retryAfter = retryAfter;
  }

  getTitle(): string {
    return 'Service Unavailable';
  }
}

/**
 * 504 Gateway Timeout Error
 */
export class GatewayTimeoutError extends AppError {
  constructor(message = 'Gateway timeout', originalError?: Error, traceId?: string) {
    super(
      message,
      504,
      'GATEWAY_TIMEOUT',
      'The server took too long to respond. Please try again later.',
      originalError,
      traceId,
    );
  }

  getTitle(): string {
    return 'Gateway Timeout';
  }
}

/**
 * Network/Connection Error
 */
export class NetworkError extends AppError {
  constructor(message = 'Network error', originalError?: Error, traceId?: string) {
    super(
      message,
      0,
      'NETWORK_ERROR',
      'Unable to connect to the server. Please check your internet connection and try again.',
      originalError,
      traceId,
    );
  }

  getTitle(): string {
    return 'Connection Error';
  }
}

/**
 * Unknown/Unexpected Error
 */
export class UnknownError extends AppError {
  constructor(message = 'An unexpected error occurred', originalError?: Error, traceId?: string) {
    super(
      message,
      0,
      'UNKNOWN_ERROR',
      'An unexpected error occurred. Please try again or contact support if the problem persists.',
      originalError,
      traceId,
    );
  }

  getTitle(): string {
    return 'Unexpected Error';
  }
}

/**
 * Error factory to create appropriate error from HTTP response
 */
export class ErrorFactory {
  static fromHttpError(error: unknown, traceId?: string): AppError {
    // Handle HttpErrorResponse from Angular
    if (error && typeof error === 'object' && 'status' in error) {
      const httpError = error as {
        status: number;
        statusText?: string;
        error?: unknown;
        message?: string;
        headers?: { get: (name: string) => string | null };
      };
      const status = httpError.status;
      const message = httpError.message || httpError.statusText || 'HTTP Error';
      const originalError = httpError.error instanceof Error ? httpError.error : undefined;

      // Try to get retry-after header
      const retryAfter = httpError.headers?.get('retry-after')
        ? parseInt(httpError.headers.get('retry-after')!, 10)
        : undefined;

      switch (status) {
        case 400:
          return new BadRequestError(message, originalError, traceId);
        case 401:
          return new UnauthorizedError(message, originalError, traceId);
        case 403:
          return new ForbiddenError(message, originalError, traceId);
        case 404:
          return new NotFoundError(message, originalError, traceId);
        case 409:
          return new ConflictError(message, originalError, traceId);
        case 422: {
          // Check for validation errors
          let validationErrors: Record<string, string[]> = {};
          if (
            httpError.error &&
            typeof httpError.error === 'object' &&
            'Errors' in httpError.error
          ) {
            const errors = (
              httpError.error as { Errors?: { Code: string; Message: string; Target?: string }[] }
            ).Errors;
            if (Array.isArray(errors)) {
              validationErrors = errors.reduce(
                (acc, err) => {
                  const key = err.Target || err.Code;
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(err.Message);
                  return acc;
                },
                {} as Record<string, string[]>,
              );
            }
          }
          return new ValidationError(message, validationErrors, originalError, traceId);
        }
        case 429:
          return new TooManyRequestsError(message, retryAfter, originalError, traceId);
        case 500:
          return new ServerError(message, originalError, traceId);
        case 502:
          return new BadGatewayError(message, originalError, traceId);
        case 503:
          return new ServiceUnavailableError(message, retryAfter, originalError, traceId);
        case 504:
          return new GatewayTimeoutError(message, originalError, traceId);
        case 0:
          return new NetworkError(message, originalError, traceId);
        default:
          return new UnknownError(message, originalError, traceId);
      }
    }

    // Handle native Error objects
    if (error instanceof Error) {
      return new UnknownError(error.message, error, traceId);
    }

    // Handle string errors
    if (typeof error === 'string') {
      return new UnknownError(error, undefined, traceId);
    }

    // Handle unknown objects
    return new UnknownError('An unknown error occurred', undefined, traceId);
  }
}
